import { forwardRef, type ChangeEvent, type ReactNode } from 'react';

/**
 * RwCheckbox — the DS checkbox (rw-ds.css `.rw-checkbox`). Thin wrapper over the native
 * `<label><input type="checkbox">` anatomy: the styled input IS the 16×16 box (appearance:none,
 * navy-neutral checked fill, focus ring, error/disabled states via row classes). Replaces raw
 * MUI `Checkbox`, whose default (brand-blue) tick isn't on-system. Anatomy: Storybook →
 * Components → Checkbox; governance → rw-ds-components § Checkbox.
 */
export interface RwCheckboxProps {
  checked?: boolean;
  defaultChecked?: boolean;
  onChange?: (checked: boolean, event: ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  /** Error edge + label/subtext critical colour (rw-ds.css `.rw-checkbox--error`). */
  error?: boolean;
  label?: ReactNode;
  /** Non-colour error/help signal under the label. */
  subtext?: ReactNode;
  name?: string;
  value?: string;
  className?: string;
  'aria-label'?: string;
}

const RwCheckbox = forwardRef<HTMLInputElement, RwCheckboxProps>(function RwCheckbox(
  { checked, defaultChecked, onChange, disabled = false, error = false, label, subtext, name, value, className, ...rest },
  ref
) {
  const rowClasses = ['rw-checkbox', error ? 'rw-checkbox--error' : '', disabled ? 'rw-checkbox--disabled' : '', className ?? '']
    .filter(Boolean)
    .join(' ');

  const hasBody = label != null || subtext != null;

  return (
    <label className={rowClasses}>
      <input
        ref={ref}
        type="checkbox"
        className="rw-checkbox__input"
        checked={checked}
        defaultChecked={defaultChecked}
        disabled={disabled}
        name={name}
        value={value}
        onChange={(e) => onChange?.(e.target.checked, e)}
        {...rest}
      />
      {hasBody && (
        <span className="rw-checkbox__body">
          {label != null && <span className="rw-checkbox__label">{label}</span>}
          {subtext != null && <span className="rw-checkbox__subtext">{subtext}</span>}
        </span>
      )}
    </label>
  );
});

export default RwCheckbox;
// Named alias — see RwButton.tsx for why this stays alongside the default export.
export { RwCheckbox };
